
import Vue from 'vue';
import _isEmpty from 'lodash/isEmpty';
import _shuffle from 'lodash/shuffle';
import { mapActions, mapState } from 'vuex';
import { ROUTER } from '@/router/constants';
import {
	RUNNITS_ACCESS_LEVEL,
	RUNNIT_NODE_DEF_TOOL_TYPE,
	RUNNIT_TYPE,
	RunnitNodeDef,
	RunnitNodeRunResult,
	RunnitNodeRun,
	RUNNIT_NODE_DEF_TOOL_APP_TYPE,
	Runnit,
} from '@run-diffusion/shared';
import { RUNNITS_OWNER_SELECTION, SELECTED_COLUMNS_MAP, FEATURED_RUNNIT_NODE_DEF_IDS } from '@/views/Runnits/constants';
import {
	getRunnitsHomeLibrarySelection,
	setRunnitsHomeLibrarySelection,
	RUNNIT_LIBRARY_FILTER_LOCATION,
	getRunnitsHomeBoardsSelection,
	setRunnitsHomeBoardsSelection,
} from '@/utils';
import { RunnitsCRUDMixin, TeamUserMixin, get$bindFirestoreOptions } from '@/mixins';
import OfferingCard from '@/components/OfferingCard.vue';
import EmptyState from '@/components/states/EmptyState.vue';
import RunnitCarousel from '../RunnitCarousel.vue';
import ImageGallery from '@/components/ImageGallery/ImageGallery.vue';
import RunnitResult from '../RunnitResult.vue';
import { db, Query } from '@/firebase';
import RunnitImageInfoCarouselDialog from '../RunnitImageInfoCarouselDialog.vue';
import SelectedColumnsButton from '@/components/ImageGallery/SelectedColumnsButton.vue';
import ImageModeButton from '@/components/ImageGallery/ImageModeButton.vue';
import RunnitIntentionsBar from '../RunnitIntentionsBar.vue';
import RunnitToolPromoCard from '../RunnitToolPromoCard.vue';
import RunnitBoardTile from './RunnitBoardsPage/RunnitBoardsDashboard/RunnitBoardTile.vue';
import { isWithinLimitNumRunnits } from '@/store/utils';
import { setPlatformHomePreference } from '@/utils';
import { buildFeaturedTemplatesQuery } from '@/utils/featuredTemplates';

export type LibrarySelection = 'FEATURED' | 'PERSONAL';
export default Vue.extend({
	name: 'RunnitsHome',
	metaInfo: {
		title: 'Home',
	},
	mixins: [
		RunnitsCRUDMixin,
		TeamUserMixin,
	],
	data () {
		return {
			RUNNIT_LIBRARY_FILTER_LOCATION: Object.freeze(RUNNIT_LIBRARY_FILTER_LOCATION),
			RUNNIT_TYPE,
			RUNNITS_OWNER_SELECTION,
			RUNNITS_ACCESS_LEVEL,
			RUNNIT_NODE_DEF_TOOL_TYPE,
			SELECTED_COLUMNS_MAP,
			FEATURED_RUNNIT_NODE_DEF_IDS,
			NUM_BOARDS_TO_SHOW: 50,
			addingNewRunnit: false,

			slideGroupState: null,
			hasPrev: false,

			librarySelection: 'FEATURED' as LibrarySelection,
			featuredNodeRunsPagesLoading: false as boolean,
			featuredNodeRunsLoading: false as boolean,
			isLoadingMore: false as boolean,
			featuredNodeRuns: [] as RunnitNodeRun[],
			pageSize: 50,
			usedPages: new Set<number>(),
			totalPages: 0,
			allNodeRunIds: [] as string[],
			hasMorePages: true,
			imageInfoCarouselConfig: {
				dialogOpen: false,
				nodeRun: null,
				nodeRuns: null,
				nodeRunResult: null,
			},
			boardsSelection: 'FEATURED' as 'FEATURED' | 'RECENT' | 'PERSONAL',
			featuredBoards: [] as Runnit[],
			myBoards: [] as Runnit[],
			loadingFeaturedBoards: false,
			loadingMyBoards: false,
		};
	},
	async created () {
		setPlatformHomePreference('RUNNIT');
		await this.initFeaturedNodeRuns();
		await this.loadFeaturedBoards();
		await this.loadMyBoards();
		// Initialize boards selection after data is loaded
		this.initializeBoardsSelection();
	},
	mounted () {
		this.updateArrowVisibility();
		this.librarySelection = getRunnitsHomeLibrarySelection() || 'FEATURED';

		// Add resize listener to update arrow visibility on window resize
		window.addEventListener('resize', this.updateArrowVisibility);
		// Add scroll listener for infinite scroll
		window.addEventListener('scroll', this.handleScroll, { passive: true });
	},
	beforeDestroy () {
		// Remove resize listener
		window.removeEventListener('resize', this.updateArrowVisibility);
		// Remove scroll listener
		window.removeEventListener('scroll', this.handleScroll);

		// Reset pagination state
		this.featuredNodeRuns = [];
		this.usedPages.clear();
		this.hasMorePages = true;
	},
	computed: {
		...mapState([
			'user',
			'team',
			'runnitState',
			'recentUserRunnitNodeDefInfos',
			'recentUserWorkflowRunnitInfos',
			'loadingRecentUserRunnitNodeDefInfos',
			'loadingRecentUserWorkflowRunnitInfos',
			'appOffers',
		]),
		personalFilterLabel (): string {
			return this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM ? 'Private' : 'Personal';
		},
		featuredNodeRunResults (): RunnitNodeRunResult[] {
			if (!this.featuredNodeRuns || !this.featuredNodeRuns.length) {
				return [];
			}
			return this.featuredNodeRuns.flatMap(nodeRun => nodeRun.results.filter(result => result.featured));
		},
		nodeRunByIdMap (): Record<string, RunnitNodeRun> {
			if (!this.featuredNodeRuns || !this.featuredNodeRuns.length) {
				return {};
			}
			return this.featuredNodeRuns.reduce((map: Record<string, RunnitNodeRun>, nodeRun: RunnitNodeRun) => {
				map[nodeRun.id] = nodeRun;
				return map;
			}, {});
		},
		visibleRunnitNodeDefInfos () {
			return (this.recentUserRunnitNodeDefInfos || []).filter((toolInfo) => (
				!toolInfo.nodeDef.isDeleted &&
				(
					this.user &&
					toolInfo.nodeDef.appType === RUNNIT_NODE_DEF_TOOL_APP_TYPE.RUNNIT &&
					(
						this.user.isAdmin ||
						toolInfo.nodeDef.isPublished
					)
				) &&
				(
					toolInfo.nodeDef.type !== RUNNIT_NODE_DEF_TOOL_TYPE.COMPANY ||
					(
						this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM &&
						this.team &&
						toolInfo.nodeDef.companyIds?.[this.team.companyId]
					)
				) &&
				!this.isNodeDefBlockedForTeamUser(toolInfo.nodeDef)
			)).slice(0, 5);
		},
		visibleWorkflowRunnitInfos () {
			return (this.recentUserWorkflowRunnitInfos || []).filter((workflowRunnitInfo) => (
				workflowRunnitInfo.runnit &&
				typeof workflowRunnitInfo.runnit !== 'string' &&
				!workflowRunnitInfo.runnit.deletedAt &&
				workflowRunnitInfo.runnit.type === RUNNIT_TYPE.WORKFLOW
			)).slice(0, this.NUM_BOARDS_TO_SHOW);
		},
		visibleBoards () {
			if (this.boardsSelection === 'FEATURED') {
				// Normalize to match RECENT structure: { runnit: Runnit }
				return this.featuredBoards.slice(0, this.NUM_BOARDS_TO_SHOW).map(runnit => ({ runnit }));
			} else if (this.boardsSelection === 'RECENT') {
				return this.visibleWorkflowRunnitInfos;
			} else if (this.boardsSelection === 'PERSONAL') {
				// Normalize to match RECENT structure: { runnit: Runnit }
				return this.myBoards.slice(0, this.NUM_BOARDS_TO_SHOW).map(runnit => ({ runnit }));
			}
			return [];
		},
		boardsLoading () {
			if (this.boardsSelection === 'FEATURED') {
				return this.loadingFeaturedBoards;
			} else if (this.boardsSelection === 'RECENT') {
				return this.loadingRecentUserWorkflowRunnitInfos;
			} else if (this.boardsSelection === 'PERSONAL') {
				return this.loadingMyBoards;
			}
			return false;
		},
		boardsSectionHasContent () {
			return this.visibleWorkflowRunnitInfos.length > 0 ||
				this.featuredBoards.length > 0 ||
				this.myBoards.length > 0;
		},
		hasFeaturedBoards () {
			return this.featuredBoards.length > 0;
		},
		hasRecentBoards () {
			return this.visibleWorkflowRunnitInfos.length > 0;
		},
		hasMyBoards () {
			return this.myBoards.length > 0;
		},
		currentTabHasContent () {
			if (this.boardsSelection === 'FEATURED') {
				return this.hasFeaturedBoards;
			} else if (this.boardsSelection === 'RECENT') {
				return this.hasRecentBoards;
			} else if (this.boardsSelection === 'PERSONAL') {
				return this.hasMyBoards;
			}
			return false;
		},
		availableBoardOptions () {
			const options: Array<'FEATURED' | 'RECENT' | 'PERSONAL'> = [];
			if (this.hasFeaturedBoards) {
				options.push('FEATURED');
			}
			if (this.hasRecentBoards) {
				options.push('RECENT');
			}
			if (this.hasMyBoards) {
				options.push('PERSONAL');
			}
			return options;
		},
		featuredColumns () {
			// SELECTED_COLUMNS_MAP is a display-label map, so its values are strings. Array.from's
			// `length` coerces, but the string leaks into every arithmetic use of numColumns.
			const numColumns = this.$vuetify.breakpoint.xsOnly ? 2 :
				(Number(SELECTED_COLUMNS_MAP[this.runnitState.selectedColumns]) || 4);

			const columns = Array.from({ length: numColumns }, () => []);
			const columnHeights = Array.from({ length: numColumns }, () => 0);

			if (!this.featuredNodeRunResults) return columns;

			this.featuredNodeRunResults.forEach((result, index) => {
				// Get image dimensions from result.file
				const height = result.file?.height || 1;
				const width = result.file?.width || 1;
				const aspectRatio = width / height;

				// Calculate normalized height (relative to a standard width of 1)
				const normalizedHeight = 1 / aspectRatio;

				const { shortestColumnIndex } = columnHeights.reduce((acc, height, index) => {
					return height < acc.minHeight ? { minHeight: height, shortestColumnIndex: index } : acc;
				}, { minHeight: columnHeights[0], shortestColumnIndex: 0 });

				// Add the image to the shortest column
				columns[shortestColumnIndex].push(result);

				// Update the column's total height
				columnHeights[shortestColumnIndex] += normalizedHeight;
			});

			return columns;
		},
	},
	watch: {
		user: {
			immediate: false,
			handler (newVal, oldVal) {
				if (newVal?.id !== oldVal?.id) {
					this.loadMyBoards();
				}
			},
		},
		team: {
			immediate: false,
			handler (newVal, oldVal) {
				if (newVal?.id !== oldVal?.id) {
					this.loadMyBoards();
				}
			},
		},
		'runnitState.runnitsOwnerSelection': {
			immediate: false,
			handler () {
				this.loadMyBoards();
			},
		},
		availableBoardOptions: {
			immediate: false,
			handler (newOptions) {
				// If current selection is no longer available, re-initialize
				if (newOptions.length > 0 && !newOptions.includes(this.boardsSelection)) {
					this.initializeBoardsSelection();
				}
			},
		},
	},
	methods: {
		...mapActions([
			'updateLoader',
			'updateSnackbar',
			'setUpsellDialog',
		]),
		isWithinLimitNumRunnits,
		_isEmpty,
		canEditRunnit (runnit: Runnit): boolean {
			// For featured templates, only admins can edit/delete
			if (runnit?.type === RUNNIT_TYPE.TEMPLATE && runnit?.isFeatured) {
				return !!this.user?.isAdmin;
			}
			return !!(
				runnit &&
				(
					this.hasManageAllGenerationsPermission ||
					(
						runnit.userId === this.user.id ||
						(
							[RUNNITS_ACCESS_LEVEL.SHARED, RUNNITS_ACCESS_LEVEL.RESTRICTED].includes(runnit.accessLevel) &&
							this.hasShareRunnitAssetsPermission
						)
					)
				)
			);
		},
		isBoardTileDisabled (runnit: Runnit): boolean {
			// Featured templates should never be disabled (always viewable)
			if (runnit && runnit.type === RUNNIT_TYPE.TEMPLATE && runnit.isFeatured) {
				return false;
			}
			// For other boards, check the limit
			return !isWithinLimitNumRunnits(this.$store.state, runnit?.id);
		},
		goHome () {
			this.routerPush(this.$route, this.$router, { name: ROUTER.SESSIONS });
		},
		goToAllTools () {
			this.routerPush(this.$route, this.$router, { name: ROUTER.RUNNITS_ALL_TOOLS });
		},
		goToOpenSourceApps () {
			setPlatformHomePreference('RD');
			this.routerPush(this.$route, this.$router, { name: ROUTER.SESSIONS });
		},
		goToRunnitBoards () {
			this.routerPush(this.$route, this.$router, { name: ROUTER.RUNNIT_BOARDS_PAGE });
		},
		setBoardsSelection (val: 'FEATURED' | 'RECENT' | 'PERSONAL') {
			this.boardsSelection = val;
			setRunnitsHomeBoardsSelection(val);
		},
		initializeBoardsSelection () {
			// Priority order:
			// 1. User's saved selection (from localStorage) - if it's available
			// 2. Recent boards exist -> RECENT
			// 3. Personal boards exist -> PERSONAL
			// 4. Default -> FEATURED
			const availableOptions = this.availableBoardOptions;

			if (availableOptions.length === 0) {
				// No options available, keep default
				this.boardsSelection = 'FEATURED';
				return;
			}

			const savedSelection = getRunnitsHomeBoardsSelection();
			// Only use saved selection if it's in the available options
			if (savedSelection && availableOptions.includes(savedSelection as 'FEATURED' | 'RECENT' | 'PERSONAL')) {
				this.boardsSelection = savedSelection as 'FEATURED' | 'RECENT' | 'PERSONAL';
				return;
			}

			// Check if recent boards exist
			if (this.hasRecentBoards) {
				this.boardsSelection = 'RECENT';
				return;
			}

			// Check if personal boards exist
			if (this.hasMyBoards) {
				this.boardsSelection = 'PERSONAL';
				return;
			}

			// Default to first available option (should be FEATURED if available)
			if (availableOptions.length > 0) {
				this.boardsSelection = availableOptions[0];
			}
		},
		async loadFeaturedBoards () {
			this.loadingFeaturedBoards = true;
			try {
				// Featured templates in the curated order set in Admin > Featured Templates.
				// The RECENT and PERSONAL toggles below stay newest-first: they are browse
				// views of a different set, not a curated strip.
				// index created: runnits - deletedAt Ascending isFeatured Ascending type Ascending featuredOrder Ascending __name__ Ascending
				const runnitsRef: Query = buildFeaturedTemplatesQuery(
					db.collection('runnits'),
					this.NUM_BOARDS_TO_SHOW,
				);

				const newResult: Runnit[] = await this.$bind(
					'featuredBoards',
					runnitsRef,
					get$bindFirestoreOptions(),
				);

				if (!newResult) {
					this.featuredBoards = [];
				}
			} catch (error) {
				console.error('Error loading featured boards:', error);
				this.featuredBoards = [];
			} finally {
				this.loadingFeaturedBoards = false;
			}
		},
		async loadMyBoards () {
			if (!this.user) {
				this.myBoards = [];
				return;
			}

			this.loadingMyBoards = true;
			try {
				let runnitsRef: Query;

				if (this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM && this.team) {
					// For team mode, we need to combine private, restricted, and shared boards
					// For simplicity, we'll load private boards owned by the user
					// index created: runnits - accessLevel Ascending deletedAt Ascending teamId Ascending userId Ascending createdAt Descending __name__ Descending
					runnitsRef = db.collection('runnits')
						.where('teamId', '==', this.team.id)
						.where('accessLevel', '==', RUNNITS_ACCESS_LEVEL.PRIVATE)
						.where('userId', '==', this.user.id)
						.where('deletedAt', '==', null)
						.where('type', '==', RUNNIT_TYPE.WORKFLOW)
						.orderBy('createdAt', 'desc')
						.limit(this.NUM_BOARDS_TO_SHOW);
				} else {
					// Personal runnits
					// index created: runnits - deletedAt Ascending teamId Ascending type Ascending userId Ascending createdAt Descending __name__ Descending
					runnitsRef = db.collection('runnits')
						.where('userId', '==', this.user.id)
						.where('teamId', '==', null)
						.where('deletedAt', '==', null)
						.where('type', '==', RUNNIT_TYPE.WORKFLOW)
						.orderBy('createdAt', 'desc')
						.limit(this.NUM_BOARDS_TO_SHOW);
				}

				const newResult: Runnit[] = await this.$bind(
					'myBoards',
					runnitsRef,
					get$bindFirestoreOptions(),
				);

				if (!newResult) {
					this.myBoards = [];
				}
			} catch (error) {
				console.error('Error loading my boards:', error);
				this.myBoards = [];
			} finally {
				this.loadingMyBoards = false;
			}
		},
		goToBoardsViewAll () {
			if (this.boardsSelection === 'FEATURED') {
				this.routerPush(this.$route, this.$router, { name: ROUTER.RUNNIT_BOARD_TEMPLATES_PAGE });
			} else {
				// For RECENT and PERSONAL, go to boards page
				this.routerPush(this.$route, this.$router, { name: ROUTER.RUNNIT_BOARDS_PAGE });
			}
		},
		updateArrowVisibility () {
			const slideGroup = this.$refs.slideGroup as any;

			if (slideGroup && slideGroup.$el) {
				const container = slideGroup.$el.querySelector('.v-slide-group__content');
				if (container) {
					this.hasPrev = container.scrollLeft > 0;
				}
			}
		},
		async initFeaturedNodeRuns () {
			try {
				this.featuredNodeRunsPagesLoading = true;

				// First, get total count to calculate pages
				const nodeRunsRef = db.collection('runnitNodeRuns').where('featured', '==', true);
				const snapshot = await nodeRunsRef.get();
				this.allNodeRunIds = snapshot.docs.map(doc => doc.id);
				this.totalPages = Math.ceil(this.allNodeRunIds.length / this.pageSize);

				// Load first random page
				await this.loadNextRandomPage();
			} catch (error) {
				console.error('Error fetching featured images', error);
			} finally {
				this.featuredNodeRunsPagesLoading = false;
			}
		},
		handleScroll () {
			if (!this.totalPages || this.isLoadingMore || !this.hasMorePages || this.featuredNodeRunsLoading || this.librarySelection !== 'FEATURED') {
				return;
			}

			const scrollPosition = window.scrollY + window.innerHeight;
			const threshold = document.documentElement.scrollHeight - 800; // Load more when within 800px of bottom

			if (scrollPosition > threshold) {
				this.loadNextRandomPage();
			}
		},
		async loadNextRandomPage () {
			if (!this.hasMorePages || this.featuredNodeRunsLoading || this.isLoadingMore) return;

			try {
				this.isLoadingMore = true;
				this.featuredNodeRunsLoading = true;

				// Get available pages that haven't been used yet
				const availablePages = Array.from(
					{ length: this.totalPages },
					(_, i) => i
				).filter(page => !this.usedPages.has(page));

				if (availablePages.length === 0) {
					this.hasMorePages = false;
					return;
				}

				// Pick a random available page
				const randomIndex = Math.floor(Math.random() * availablePages.length);
				const selectedPage = availablePages[randomIndex];
				this.usedPages.add(selectedPage);

				// Get IDs for this page
				const startIndex = selectedPage * this.pageSize;
				const pageIds = this.allNodeRunIds.slice(startIndex, startIndex + this.pageSize);

				// Fetch the actual documents
				const newNodeRuns = await Promise.all(
					pageIds.map(async (id) => {
						const doc = await db.collection('runnitNodeRuns').doc(id).get();
						if (!doc.exists) return null;

						let fetchedNodeDef = null;
						const data = doc.data();

						if (data.nodeDef && !data.nodeDef.title) {
							try {
								const nodeDefRef = db.doc(`runnitNodeDefs/${data.nodeDefId}`);
								const nodeDef = (await nodeDefRef.get()).data();
								fetchedNodeDef = { ...nodeDef, id: data.nodeDefId };
							} catch (err) {
								console.error('Failed to fetch the generating tool', err);
							}
						}

						return {
							...data,
							...(fetchedNodeDef ? { nodeDef: fetchedNodeDef } : {}),
							id: doc.id,
						};
					})
				);

				// Filter out null values and add to existing runs
				const validNodeRuns = newNodeRuns.filter(run => run !== null);
				this.featuredNodeRuns = [...this.featuredNodeRuns, ..._shuffle(validNodeRuns)];

				// Update hasMorePages based on available unused pages
				this.hasMorePages = this.usedPages.size < this.totalPages;

			} catch (error) {
				console.error('Error loading next page', error);
			} finally {
				this.featuredNodeRunsLoading = false;
				this.isLoadingMore = false;
			}
		},
		onNodeRunResultClick (nodeRun: RunnitNodeRun, nodeRunResult: RunnitNodeRunResult) {
			this.imageInfoCarouselConfig = {
				dialogOpen: !!(nodeRun && nodeRunResult),
				nodeRun,
				nodeRunResult,
			};
		},
		featuredResultFilter (result: RunnitNodeRunResult) {
			return result.featured;
		},
		setLibrarySelection (val) {
			this.librarySelection = val;
			setRunnitsHomeLibrarySelection(val);
		},
		async onToolCardClick (nodeDef: RunnitNodeDef) {
			await this.onAddRunnitClick(nodeDef, RUNNIT_TYPE.SINGLE_TOOL);
		},
		onNewBoardMenuSelected (menuValue: string) {
			switch (menuValue) {
				case 'from-scratch':
					this.onAddRunnitClick();
					break;
				case 'from-template':
					this.$router.push({ name: ROUTER.RUNNIT_BOARD_TEMPLATES_PAGE });
					break;
				default:
					break;
			}
		},
		handleIsPublicUpdated (payload: { nodeRunResult: RunnitNodeRunResult, nodeRun: RunnitNodeRun }) {
			this.imageInfoCarouselConfig.nodeRun = { ...payload.nodeRun };
			this.imageInfoCarouselConfig.nodeRunResult = { ...payload.nodeRunResult };
		},
	},
	components: {
		EmptyState,
		OfferingCard,
		RunnitCarousel,
		ImageGallery,
		RunnitResult,
		RunnitImageInfoCarouselDialog,
		SelectedColumnsButton,
		ImageModeButton,
		RunnitIntentionsBar,
		RunnitToolPromoCard,
		RunnitBoardTile,
	},
});
